import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Flex, Text, Input, IconButton, Spinner, VStack, Icon, Portal,
  Popover, PopoverTrigger, PopoverContent, PopoverBody, useDisclosure, useToast,
  Menu, MenuButton, MenuList, MenuItem,
} from '@chakra-ui/react';
import {
  LuSparkles, LuX, LuArrowUp, LuMaximize2, LuMinus, LuHistory, LuSearch,
  LuEllipsis, LuTrash2,
} from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  sendMessage, getSessions, getSessionMessages, clearSession, CHAT_SESSION_KEY,
} from './chatService';
import ChatMarkdown from './chatMarkdown';
import { ChatRecordsProvider } from './ChatRecordsContext';
import { recordAt, recordLocation } from './chatRecords';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import { formatRelativeShort } from '../../utils/intlFormatters';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useFilters } from '../../contexts/FilterContext';
import { useDetailPanel } from '../../hooks/useDetailPanel';
import { useStickToBottom } from '../../hooks/useStickToBottom';
import { SEARCH_THRESHOLD, filterSessions } from './sessionList';
import { chatOwnerName } from './chatAudience';

// The set of docked conversations, so they survive a remount — the widget is
// unmounted on `/chat` and on every route change, and losing every open pill
// when the user peeks at the full page would defeat the point of docking.
// Only identity is stored; messages are re-fetched when a pill is expanded.
const OPEN_THREADS_KEY = 'mikapital.chat.openThreads';

// Set by /chat when the user sends a conversation back to the panel: the
// widget picks it up on mount and opens it expanded, not as a pill.
export const DOCK_REQUEST_KEY = 'mikapital.chat.dockRequest';

// Sent by the sidebar ("Ask" and ⌘J) to open the panel: on the last
// conversation if one is docked, on a new one otherwise.
export const OPEN_ASSISTANT_EVENT = 'assistant:open';

const readOpenThreads = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(OPEN_THREADS_KEY) || '[]');
    if (!Array.isArray(saved)) return [];
    return saved
      .filter((s) => s && s.sessionId)
      .map((s, i) => ({
        id: i + 1,
        sessionId: s.sessionId,
        title: s.title || null,
        messages: [],
        input: '',
        loading: false,
        loaded: false,
      }));
  } catch (e) {
    return [];
  }
};

// Height of the assistant bar. The layout reserves this much padding so the
// bar never covers page content — keep the two in step.
export const DOCK_H = 48;

// A message as it is kept in a thread. `records` and `searches` are what the
// backend kept with an answer: what its rows show and what it changed (MI-343).
export const toMessage = (m) => ({
  role: m.role,
  content: m.content,
  records: m.records || [],
  searches: m.searches || [],
});

// MI-299: the assistant supports multiple concurrently-open
// conversations (Linear-style). Each open thread is either the single expanded
// panel or a docked pill bottom-right; a history popup lists past sessions so
// the user never has to leave the page to revisit a conversation.
//
// A thread: { id, sessionId, title, messages, input, loading, loaded }. `id` is
// a local counter; `sessionId` is null until the first message creates a server
// session; `loaded` tracks whether the messages came back from the server.
export default function AssistantWidget({ sidebarOffset = 0 }) {
  const { t } = useTranslation('chat');
  const navigate = useNavigate();
  const toast = useToast();
  const owner = chatOwnerName(useWorkspace().current);

  const [threads, setThreads] = useState(readOpenThreads);
  const [expandedId, setExpandedId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [search, setSearch] = useState('');
  const [panelHeight, setPanelHeight] = useState(520); // desktop panel height (px)
  const [deleting, setDeleting] = useState(false);
  // MI-343: the record the page's open detail panel shows, so the chat can
  // mark its row.
  const { showSearch } = useFilters();
  const { width: detailPanelW, address: panelAddress } = useDetailPanel();
  const openRecordKey = detailPanelW > 0 ? recordAt(panelAddress) : null;
  const history = useDisclosure();
  const confirmDelete = useDisclosure();

  const idRef = useRef(threads.length);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const dockHandled = useRef(false);

  const expanded = threads.find((th) => th.id === expandedId) || null;
  const minimized = threads.filter((th) => th.id !== expandedId);

  const updateThread = useCallback((id, patch) => {
    setThreads((ts) => ts.map((th) => (
      th.id === id ? { ...th, ...(typeof patch === 'function' ? patch(th) : patch) } : th
    )));
  }, []);

  // Pill / header label: prefer the server title, fall back to the first user
  // message, then to the generic "New conversation".
  const threadLabel = useCallback((th) => {
    if (th.title) return th.title;
    const firstUser = th.messages.find((m) => m.role === 'user');
    if (firstUser) return firstUser.content.slice(0, 48);
    return t('assistant.newChat');
  }, [t]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => { if (expanded) scrollToBottom(); }, [expanded, expanded?.messages, expanded?.loading, scrollToBottom]);
  useStickToBottom(scrollRef, expandedId);
  useEffect(() => { if (expandedId) inputRef.current?.focus(); }, [expandedId]);

  // Mirror the currently-expanded thread's session so the full /chat page stays
  // in sync with what the user is actively looking at.
  // A thread with no session yet (nothing sent) must clear the mirror too —
  // otherwise expanding an empty chat lands on the previous conversation.
  useEffect(() => {
    if (!expanded) return;
    if (expanded.sessionId) localStorage.setItem(CHAT_SESSION_KEY, expanded.sessionId);
    else localStorage.removeItem(CHAT_SESSION_KEY);
  }, [expanded, expanded?.sessionId, expandedId]);

  // Keep the docked set on disk (identity only — see OPEN_THREADS_KEY).
  useEffect(() => {
    const open = threads
      .filter((th) => th.sessionId)
      .map((th) => ({ sessionId: th.sessionId, title: th.title }));
    localStorage.setItem(OPEN_THREADS_KEY, JSON.stringify(open));
  }, [threads]);

  // --- Thread lifecycle ---------------------------------------------------
  const openNewThread = () => {
    const id = idRef.current + 1;
    idRef.current = id;
    setThreads((ts) => [...ts, {
      id, sessionId: null, title: null, messages: [], input: '', loading: false, loaded: true,
    }]);
    setExpandedId(id);
  };

  // Expand a docked pill, pulling its messages from the server the first time
  // (a restored pill starts with none).
  // Pull a restored thread's messages from the server (pills are persisted by
  // identity only, so their history has to be re-fetched on first open).
  const loadThreadMessages = useCallback(async (id, sessionId) => {
    updateThread(id, { loading: true });
    try {
      const data = await getSessionMessages(sessionId);
      updateThread(id, {
        messages: (data.messages || []).map(toMessage),
        loading: false,
        loaded: true,
      });
    } catch (e) {
      updateThread(id, {
        loading: false, loaded: true,
        messages: [{ role: 'assistant', content: t('toasts.loadFailed') }],
      });
    }
  }, [updateThread, t]);

  const expandThread = (id) => {
    setExpandedId(id);
    const th = threads.find((x) => x.id === id);
    if (!th || th.loaded || !th.sessionId) return;
    loadThreadMessages(id, th.sessionId);
  };

  // Coming back from /chat via its dock button: open that conversation as the
  // expanded panel. It is already among the restored threads (both views share
  // OPEN_THREADS_KEY), so this only has to pick it and clear the request.
  useEffect(() => {
    if (dockHandled.current) return;
    const wanted = localStorage.getItem(DOCK_REQUEST_KEY);
    if (!wanted) return;
    dockHandled.current = true;
    localStorage.removeItem(DOCK_REQUEST_KEY);
    const th = threads.find((x) => x.sessionId === wanted);
    if (th) { expandThread(th.id); return; }
    // Not docked yet — the user reached /chat and switched conversations
    // there. Adopt it so the panel opens on what they were just reading.
    const id = ++idRef.current;
    setThreads((ts) => [...ts, {
      id, sessionId: wanted, title: null, messages: [], input: '',
      loading: false, loaded: false,
    }]);
    setExpandedId(id);
    loadThreadMessages(id, wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads]);

  useEffect(() => {
    const onOpen = () => {
      const last = threads[threads.length - 1];
      if (last) expandThread(last.id);
      else openNewThread();
    };
    window.addEventListener(OPEN_ASSISTANT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads]);

  const openSession = async (session) => {
    history.onClose();
    const existing = threads.find((th) => th.sessionId === session.id);
    if (existing) { expandThread(existing.id); return; }
    const id = idRef.current + 1;
    idRef.current = id;
    setThreads((ts) => [...ts, {
      id, sessionId: session.id, title: session.title, messages: [], input: '', loading: true, loaded: false,
    }]);
    setExpandedId(id);
    try {
      const data = await getSessionMessages(session.id);
      updateThread(id, {
        messages: (data.messages || []).map(toMessage),
        loading: false,
        loaded: true,
      });
    } catch (e) {
      updateThread(id, {
        loading: false, loaded: true,
        messages: [{ role: 'assistant', content: t('toasts.loadFailed') }],
      });
    }
  };

  // Minimizing an empty chat (nothing asked yet) discards it rather than
  // leaving a stray "New conversation" pill in the dock.
  const minimizeThread = () => {
    if (expanded && expanded.messages.length === 0) {
      closeThread(expanded.id);
      return;
    }
    setExpandedId(null);
  };

  const closeThread = (id) => {
    setThreads((ts) => ts.filter((th) => th.id !== id));
    if (expandedId === id) setExpandedId(null);
  };

  // Going full-page collapses the thread to a pill rather than dropping it, so
  // coming back to any other page still has the conversation docked. An empty
  // thread has nothing to dock — drop it and open /chat blank instead.
  const openFull = () => {
    if (expanded && !expanded.sessionId) closeThread(expandedId);
    else setExpandedId(null);
    navigate('/chat');
  };

  const handleDeleteThread = async () => {
    if (!expanded?.sessionId) return;
    setDeleting(true);
    try {
      await clearSession(expanded.sessionId);
      if (localStorage.getItem(CHAT_SESSION_KEY) === expanded.sessionId) {
        localStorage.removeItem(CHAT_SESSION_KEY);
      }
      setSessions((ss) => ss.filter((s) => s.id !== expanded.sessionId));
      closeThread(expanded.id);
      toast({ title: t('toasts.deletedTitle'), status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({
        title: t('toasts.errorTitle'), description: t('toasts.clearFailed'),
        status: 'error', duration: 3000, isClosable: true,
      });
    } finally {
      setDeleting(false);
      confirmDelete.onClose();
    }
  };

  // Drag the top edge to grow/shrink the panel. It's anchored at the bottom, so
  // dragging up extends it upward. Desktop only (mobile is full-screen).
  const startResize = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = panelHeight;
    const onMove = (ev) => {
      const max = Math.round(window.innerHeight * 0.9);
      setPanelHeight(Math.min(Math.max(startH + (startY - ev.clientY), 360), max));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleSend = async (thread) => {
    const text = (thread.input || '').trim();
    if (!text || thread.loading) return;
    updateThread(thread.id, (th) => ({
      messages: [...th.messages, { role: 'user', content: text }], input: '', loading: true,
    }));
    const errMsg = t('assistant.error');
    try {
      const res = await sendMessage(thread.sessionId, text);
      updateThread(thread.id, (th) => ({
        sessionId: th.sessionId || res?.session_id || null,
        // Label the docked pill even after a remount, when the messages are gone.
        title: th.title || text.slice(0, 48),
        messages: [...th.messages, toMessage({
          role: 'assistant', content: res?.response || '',
          records: res?.records, searches: res?.searches,
        })],
        loading: false,
      }));
    } catch (e) {
      updateThread(thread.id, (th) => ({
        messages: [...th.messages, { role: 'assistant', content: errMsg }], loading: false,
      }));
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (expanded) handleSend(expanded); }
  };

  // --- Records (MI-343) ---------------------------------------------------
  // A click on a record opens it in its own page. The panel stays open where
  // it is, with its conversation as it was: the page changes behind it, and
  // the reader minimizes the chat to see the whole detail panel.
  const openRecord = useCallback((type, id, options) => {
    const to = recordLocation(type, id, window.location, options);
    if (to) navigate(to);
  }, [navigate]);

  // "See all in Movements": the list, on the search the rows came from.
  const openSearch = useCallback((search) => {
    showSearch(search);
    navigate('/movements');
  }, [showSearch, navigate]);

  // --- History popup ------------------------------------------------------
  const loadSessions = async () => {
    try {
      const data = await getSessions();
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      setSessions([]);
    }
  };

  const filteredSessions = useMemo(() => filterSessions(sessions, search), [sessions, search]);

  const HistoryButton = (
    <Popover
      isOpen={history.isOpen}
      onOpen={() => { history.onOpen(); setSearch(''); loadSessions(); }}
      onClose={history.onClose}
      placement="top-end"
      isLazy
    >
      <PopoverTrigger>
        <IconButton
          icon={<LuHistory />}
          size="sm"
          variant="ghost"
          borderRadius="md"
          color="redesign.heading"
          flexShrink={0}
          _hover={{ bg: 'redesign.bg.card' }}
          aria-label={t('assistant.history')}
        />
      </PopoverTrigger>
      {/* Portalled so the collapsed launcher's overflow can't clip it. */}
      <Portal>
        <PopoverContent
          bg="redesign.bg.surface"
          borderColor="redesign.border"
          boxShadow="2xl"
          w="320px"
          _focus={{ outline: 'none' }}
        >
          <PopoverBody p={2}>
            <Text px={2} py={1} fontSize="xs" fontWeight="700" color="redesign.text.muted" textTransform="uppercase" letterSpacing="wide">
              {t('history.title')}
            </Text>
            {sessions.length >= SEARCH_THRESHOLD && (
              <Flex align="center" gap={2} px={2} py={1} mb={1}>
                <Icon as={LuSearch} boxSize={3.5} color="redesign.text.muted" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('history.searchPlaceholder')}
                  variant="unstyled"
                  size="sm"
                  fontSize="sm"
                />
              </Flex>
            )}
            {filteredSessions.length === 0 ? (
              <Text px={2} py={3} fontSize="sm" color="redesign.text.muted">{t('history.empty')}</Text>
            ) : (
              <VStack align="stretch" spacing={0} maxH="300px" overflowY="auto">
                {filteredSessions.map((s) => (
                  <Flex
                    key={s.id}
                    as="button"
                    onClick={() => openSession(s)}
                    align="center"
                    justify="space-between"
                    gap={3}
                    px={2}
                    py="6px"
                    borderRadius="md"
                    textAlign="left"
                    _hover={{ bg: 'redesign.bg.card' }}
                  >
                    <Text fontSize="sm" color="redesign.heading" noOfLines={1}>{s.title}</Text>
                    <Text fontSize="xs" color="redesign.text.muted" flexShrink={0}>
                      {formatRelativeShort(s.updated_at)}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );

  // --- Dock: the assistant bar --------------------------------------------
  // Permanent chrome pinned to the bottom of the content area (Linear-style):
  // minimized chats, then the launcher, then history — all flush right. On a
  // computer it sits on the window's chrome below the content panel, like a
  // status bar. It stays put while the panel is open, which is why it isn't
  // tied to `expanded`.
  const dock = (
      <Flex
        role="group"
        position="fixed"
        bottom={0}
        right={0}
        left={{ base: 0, md: `${sidebarOffset}px` }}
        h={`${DOCK_H}px`}
        px={3}
        bg={{ base: 'redesign.bg.page', md: 'redesign.bg.chrome' }}
        zIndex={1000}
        align="center"
        gap={2}
        justify="flex-end"
      >
        {minimized.length > 0 && (
          <Flex
            align="center"
            gap={1}
            maxW={{ base: 'calc(100vw - 9rem)', md: '50vw' }}
            overflowX="auto"
            sx={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
          >
            {minimized.map((th) => (
              <Flex
                key={th.id}
                role="group"
                align="center"
                flexShrink={0}
                borderRadius="full"
                pl={3}
                pr={1}
                py="5px"
                cursor="pointer"
                _hover={{ bg: 'redesign.bg.card' }}
                transition="background 0.12s"
                onClick={() => expandThread(th.id)}
              >
                <Text fontSize="sm" fontWeight="600" color="redesign.heading" noOfLines={1} maxW="150px" pl={1}>
                  {threadLabel(th)}
                </Text>
                <IconButton
                  icon={<LuX />} size="xs" variant="ghost" ml={1}
                  opacity={0} _groupHover={{ opacity: 1 }}
                  onClick={(e) => { e.stopPropagation(); closeThread(th.id); }}
                  aria-label={t('assistant.close')}
                />
              </Flex>
            ))}
          </Flex>
        )}

        <Flex
          as="button"
          onClick={openNewThread}
          align="center"
          h="28px"
          borderRadius="md"
          color="redesign.heading"
          flexShrink={0}
          overflow="hidden"
          _hover={{ bg: 'redesign.bg.card' }}
          transition="all 0.15s"
          aria-label={t('assistant.launcher')}
          title={t('assistant.launcher')}
          data-tour="tour-assistant"
        >
          <Icon as={LuSparkles} boxSize={4} mx={2} color="redesign.accent.blue" flexShrink={0} />
          <Text fontSize="sm" fontWeight="600" pr={2} whiteSpace="nowrap">
            {t('assistant.launcher')}
          </Text>
        </Flex>

        {HistoryButton}
      </Flex>
  );

  if (!expanded) return dock;

  // --- Panel (expanded) ---------------------------------------------------
  // Sits above the bar rather than over it.
  const panel = (
    <Flex
      position="fixed"
      bottom={{ base: `${DOCK_H}px`, md: `${DOCK_H + 8}px` }}
      right={{ base: 0, md: 6 }}
      zIndex={1000}
      direction="column"
      w={{ base: '100vw', md: '380px' }}
      // MI-311: on phones the panel is anchored above the dock, so its height
      // has to exclude the dock too — at a full 100vh the top edge (and with it
      // the minimize / expand / close controls) sat off-screen at -DOCK_H.
      h={{ base: `calc(100vh - ${DOCK_H}px)`, md: `${panelHeight}px` }}
      maxH={{ base: `calc(100vh - ${DOCK_H}px)`, md: '90vh' }}
      bg="redesign.bg.surface"
      borderWidth="1px"
      borderColor="redesign.border"
      borderRadius={{ base: 0, md: 'xl' }}
      boxShadow="2xl"
      overflow="hidden"
    >
      {/* Resize handle — drag the top edge to grow the panel (desktop only) */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="8px"
        cursor="ns-resize"
        zIndex={3}
        onMouseDown={startResize}
      />

      {/* Header */}
      <Flex
        align="center"
        justify="space-between"
        px={4}
        py={3}
        borderBottom="1px solid"
        borderColor="redesign.border"
        flexShrink={0}
      >
        <Flex align="center" gap={2} minW={0}>
          <Icon as={LuSparkles} color="redesign.accent.blue" boxSize={4} flexShrink={0} />
          <Text fontSize="sm" fontWeight="600" color="redesign.heading" noOfLines={1}>
            {expanded.messages.length > 0 ? threadLabel(expanded) : t('assistant.title')}
          </Text>
          {/* Conversation actions live here (Linear-style), left of the window
              controls — deleting is about the chat, not about the panel. */}
          <Menu placement="bottom-start" isLazy>
            <MenuButton
              as={IconButton}
              icon={<LuEllipsis />}
              size="xs"
              variant="ghost"
              flexShrink={0}
              isDisabled={!expanded.sessionId}
              aria-label={t('assistant.more')}
              _hover={{ bg: 'redesign.bg.bubble' }}
              _active={{ bg: 'redesign.bg.bubble' }}
            />
            <MenuList minW="180px">
              <MenuItem
                icon={<LuTrash2 />}
                color="redesign.accent.red"
                // Chakra's cool grey highlight reads blue here; match the chat.
                _hover={{ bg: 'redesign.bg.bubble' }}
                _focus={{ bg: 'redesign.bg.bubble' }}
                onClick={confirmDelete.onOpen}
              >
                {t('assistant.delete')}
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
        <Flex align="center" gap={1} flexShrink={0}>
          <IconButton
            icon={<LuMinus />} size="xs" variant="ghost"
            onClick={minimizeThread} aria-label={t('assistant.minimize')}
          />
          <IconButton
            icon={<LuMaximize2 />} size="xs" variant="ghost"
            onClick={openFull} aria-label={t('assistant.openFull')}
          />
          <IconButton
            icon={<LuX />} size="xs" variant="ghost"
            onClick={() => closeThread(expanded.id)} aria-label={t('assistant.close')}
          />
        </Flex>
      </Flex>

      {/* Messages */}
      <Box ref={scrollRef} flex="1" overflowY="auto" px={4} py={3}>
        {expanded.messages.length === 0 && !expanded.loading ? (
          <VStack spacing={2} justify="center" h="100%" textAlign="center" color="redesign.text.muted">
            <Icon as={LuSparkles} boxSize={7} color="redesign.accent.blue" />
            <Text fontSize="sm" fontWeight="600" color="redesign.heading">
              {owner ? t('assistant.welcomeFor', { owner }) : t('assistant.welcome')}
            </Text>
            <Text fontSize="xs">{t('assistant.welcomeSubtitle')}</Text>
          </VStack>
        ) : (
          <ChatRecordsProvider
            messages={expanded.messages}
            onOpenRecord={openRecord}
            onOpenSearch={openSearch}
            openKey={openRecordKey}
          >
            <VStack align="stretch" spacing={3}>
              {expanded.messages.map((m, i) => (
                m.role === 'user' ? (
                  <Box
                    key={i}
                    alignSelf="flex-end"
                    maxW="85%"
                    bg="redesign.bg.bubble"
                    color="redesign.heading"
                    px={3}
                    py={2}
                    borderRadius="lg"
                    fontSize="sm"
                  >
                    <Text whiteSpace="pre-wrap">{m.content}</Text>
                  </Box>
                ) : (
                  // An answer takes the panel's whole width, with no bubble: its
                  // cards of records need the room, and they are boxes already.
                  <Box
                    key={i}
                    color="redesign.text"
                    fontSize="sm"
                    sx={{ '& > * > *:last-child': { marginBottom: 0 } }}
                  >
                    <ChatMarkdown
                      content={m.content} records={m.records} searches={m.searches} position={i}
                    />
                  </Box>
                )
              ))}
              {expanded.loading && (
                <Flex align="center" gap={2} color="redesign.text.muted" fontSize="sm">
                  <Spinner size="xs" /> <Text>{t('thinking')}</Text>
                </Flex>
              )}
            </VStack>
          </ChatRecordsProvider>
        )}
      </Box>

      {/* Input */}
      <Flex px={3} py={3} gap={2} borderTop="1px solid" borderColor="redesign.border" flexShrink={0}>
        <Input
          ref={inputRef}
          value={expanded.input}
          onChange={(e) => updateThread(expanded.id, { input: e.target.value })}
          onKeyDown={onKeyDown}
          placeholder={t('assistant.placeholder')}
          size="sm"
          borderRadius="full"
        />
        <IconButton
          icon={<LuArrowUp />}
          size="sm"
          bg="redesign.bg.surface"
          color="redesign.heading"
          borderWidth="1px"
          borderColor="redesign.border"
          _hover={{ bg: 'redesign.bg.bubble' }}
          borderRadius="full"
          onClick={() => handleSend(expanded)}
          isDisabled={!expanded.input.trim() || expanded.loading}
          aria-label={t('send')}
        />
      </Flex>

      <ConfirmationDialog
        isOpen={confirmDelete.isOpen}
        onClose={confirmDelete.onClose}
        onConfirm={handleDeleteThread}
        title={t('deleteDialog.title')}
        message={t('deleteDialog.message')}
        confirmText={t('deleteDialog.confirm')}
        confirmColorScheme="red"
        isLoading={deleting}
      />
    </Flex>
  );

  return (<>{dock}{panel}</>);
}
